import type { TournamentParams } from './types'

export type CreateTournamentParams = TournamentParams & {
  groupPlayEnabled?: boolean
  groupPlayConfig?: {
    structureMode?: 'auto' | 'manual'
    numberOfGroups?: number
    advancePerGroup?: number
    targetBracketSize?: number
    teamsPerGroup?: number
    playInEnabled?: boolean
  }
  matchFormats?: {
    grandFinals?: 'bo3' | 'bo5'
  }
}

/**
 * Multipart body for POST /admin/create/tournament. Pure so it can be unit tested.
 * Field order and conditions are unchanged from the original thunk; the only
 * additions are team_formation, draft_settings and region_settings.
 */
export const buildCreateTournamentFormData = ({
  avatar,
  cover_photo,
  tier,
  game,
  play_format,
  tournament_status,
  location,
  tournament_name,
  tournament_description,
  prize_pool,
  number_of_teams,
  bracket,
  startDate,
  endDate,
  max_player_size,
  transactionHash,
  prize_positions,
  contributors,
  payment_method,
  max_draft_players,
  players_per_team,
  groupPlayEnabled,
  groupPlayConfig,
  matchFormats,
  communityId,
  team_formation,
  draft_settings,
  region_settings,
}: CreateTournamentParams): FormData => {
  const params = new FormData()
  if (avatar) params.append('avatar', avatar)
  if (cover_photo) params.append('cover_photo', cover_photo)
  if (tier) params.append('tier', tier)
  if (game) params.append('game', game)
  if (play_format) params.append('play_format', play_format)
  if (location) params.append('location', location)
  if (tournament_name) params.append('tournament_name', tournament_name)
  if (tournament_status)
    params.append('tournament_status', tournament_status)
  if (tournament_description)
    params.append('tournament_description', tournament_description)
  if (prize_pool) params.append('prize_pool', prize_pool)
  if (number_of_teams) params.append('number_of_teams', number_of_teams)
  if (bracket) params.append('bracket', bracket)
  if (startDate)
    params.append('date.start', new Date(startDate).toISOString())
  if (endDate) params.append('date.end', new Date(endDate).toISOString())
  if (max_player_size) params.append('max_player_size', max_player_size)
  if (transactionHash) params.append('transactionHash', transactionHash)
  if (prize_positions)
    params.append('prize_positions', JSON.stringify(prize_positions))
  if (contributors)
    params.append('contributors', JSON.stringify(contributors))

  // Add payment method (defaults to leet_token if not specified)
  params.append('payment_method', payment_method || 'leet_token')

  // Add draft mode fields
  if (max_draft_players) params.append('max_draft_players', String(max_draft_players))
  if (players_per_team) params.append('players_per_team', String(players_per_team))

  // Team formation (captain draft / region teams). Team-signup tournaments send none of these.
  if (team_formation) params.append('team_formation', team_formation)
  if (draft_settings) params.append('draft_settings', JSON.stringify(draft_settings))
  if (region_settings) params.append('region_settings', JSON.stringify(region_settings))

  // Add group play fields
  if (groupPlayEnabled !== undefined) {
    params.append('groupPlayEnabled', String(groupPlayEnabled))
  }
  if (groupPlayConfig) {
    params.append('groupPlayConfig', JSON.stringify(groupPlayConfig))
  }
  if (matchFormats) {
    params.append('matchFormats', JSON.stringify(matchFormats))
  }

  // Add community tournament field
  if (communityId) {
    params.append('communityId', communityId)
  }

  return params
}
